import { type ReactNode, useCallback, useRef, useState, useEffect, JSX } from "react";
import { Container, TitleContainer, Title, Chevron, ContentWrapper, Content } from "./style";

export interface AccordionProps {
  title: string;
  children?: ReactNode;
  startsExpanded?: boolean;
  hasChevron?: boolean;
  infoText?: string;
}

const AccordionOverview = ({ title, children, startsExpanded, hasChevron = true, infoText }: AccordionProps): JSX.Element => {
  const [isExpanded, setExpand] = useState<boolean>(startsExpanded || false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (!isExpanded) {
      setContentHeight(0);
      return;
    }

    const updateHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };

    // Update height initially when expanded
    updateHeight();

    // Use ResizeObserver to watch for content changes
    const resizeObserver = new ResizeObserver(updateHeight);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Cleanup observer on unmount or when collapsed
    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded, children]);

  const handleExpandToggle = useCallback(() => {
    setExpand(!isExpanded);
  }, [isExpanded]);

  return (
    <Container>
      <TitleContainer $isExpanded={isExpanded}>
        <Title onClick={handleExpandToggle}>
          {title}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: "400" }}>{infoText}{hasChevron && <Chevron direction={isExpanded ? "top" : "bottom"} />}</div>
        </Title>
      </TitleContainer>
      <ContentWrapper $maxHeight={contentHeight}>
        <Content ref={contentRef}>{children}</Content>
      </ContentWrapper>
    </Container>
  );
};


export default AccordionOverview;

